import React, { useMemo } from "react"

import { ChartFigure, type ChartTable } from "@coval/ui/chart-figure"
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import {
  PRIMARY_BAR_SERIES_COLOR,
  seriesChipTones,
} from "@/components/charts/seriesPalette"

interface MiniCompareBarChartDatum {
  id: string
  label: string
  value: number
  color?: string
}

interface MiniCompareBarChartProps {
  data: MiniCompareBarChartDatum[]
  height?: number
  valueLabel?: string
  /** Format value in tooltip and axis (e.g. "0%" or "0.00") */
  formatValue?: (v: number) => string
  /** Accessible name of the chart; defaults to the value label by group. */
  label?: string
}

const DEFAULT_BAR_COLOR = PRIMARY_BAR_SERIES_COLOR
const tickLabelColor = "var(--coval-text-muted)"
const dividerColor = "var(--coval-border)"

const MiniCompareXAxisTick = ({
  x,
  y,
  payload,
  chartData,
}: {
  x?: number
  y?: number
  payload?: { value: string }
  chartData: Array<{ id: string; displayLabel: string }>
}) => {
  const entry = chartData.find((d) => d.id === payload?.value)
  const text = entry?.displayLabel ?? payload?.value
  return (
    <text
      x={x}
      y={y}
      dy={4}
      textAnchor="middle"
      fontSize={10}
      fill={tickLabelColor}
    >
      {text}
    </text>
  )
}

/**
 * Compact vertical bar chart for compare-by (e.g. one bar per agent/persona with value = avg or %).
 */
export const MiniCompareBarChart: React.FC<MiniCompareBarChartProps> = ({
  data,
  height = 164,
  valueLabel = "Value",
  formatValue = (v) => String(v),
  label,
}) => {
  const chartLabel = label ?? `${valueLabel} by group`
  const { chartData, yMin, yMax } = useMemo(() => {
    if (data.length === 0) {
      return { chartData: [], yMin: 0, yMax: 1 }
    }
    const values = data.map((d) => d.value)
    const minVal = Math.min(...values, 0)
    const maxVal = Math.max(...values, 0)
    const yMin = minVal < 0 ? minVal * 1.1 : 0
    const yMax = maxVal <= 0 ? 1 : maxVal * 1.1
    return {
      chartData: data.map((d) => ({
        ...d,
        displayLabel:
          d.label.length > 12 ? `${d.label.slice(0, 11)}…` : d.label,
      })),
      yMin,
      yMax,
    }
  }, [data])

  const summary = useMemo(() => {
    if (data.length === 0) {
      return undefined
    }
    const sorted = [...data].sort((a, b) => b.value - a.value)
    const high = sorted[0]
    const low = sorted[sorted.length - 1]
    if (sorted.length === 1) {
      return `${high.label}: ${formatValue(high.value)}.`
    }
    return `Highest is ${high.label} at ${formatValue(high.value)}; lowest is ${low.label} at ${formatValue(low.value)}.`
  }, [data, formatValue])

  const table = useMemo(
    (): ChartTable => ({
      columns: [
        { key: "label", header: "Group" },
        { key: "value", header: valueLabel, numeric: true },
      ],
      rows: data.map((row) => ({
        key: row.id,
        cells: { label: row.label, value: formatValue(row.value) },
      })),
    }),
    [data, formatValue, valueLabel],
  )

  if (chartData.length === 0) {
    return null
  }

  return (
    <ChartFigure
      label={chartLabel}
      summary={summary}
      table={table}
      className="w-full mt-1"
      style={{ height }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          role="img"
          aria-label={chartLabel}
          data={chartData}
          margin={{ top: 5, right: 5, left: 10, bottom: 10 }}
          barCategoryGap={4}
          barGap={2}
        >
          <XAxis
            dataKey="id"
            axisLine={{ stroke: dividerColor, strokeWidth: 1 }}
            tickLine={false}
            tick={<MiniCompareXAxisTick chartData={chartData} />}
            interval={0}
            height={30}
          />
          <YAxis
            axisLine={{ stroke: dividerColor, strokeWidth: 1 }}
            tickLine={false}
            tick={{ fontSize: 10, fill: tickLabelColor }}
            width={40}
            domain={[yMin, yMax]}
            tickFormatter={formatValue}
          />
          <Tooltip
            formatter={(value: number) => [formatValue(value), valueLabel]}
            labelFormatter={(id: string) =>
              chartData.find((d) => d.id === id)?.label ?? id
            }
            contentStyle={{
              borderRadius: "6px",
              fontSize: "12px",
              padding: "4px 8px",
              backgroundColor: "var(--surface-default)",
              border: `1px solid ${dividerColor}`,
              color: "var(--coval-text)",
            }}
            labelStyle={{
              color: "var(--coval-text)",
            }}
            itemStyle={{
              color: "var(--coval-text)",
            }}
            cursor={{ fill: dividerColor }}
          />
          <Bar dataKey="value" radius={[2, 2, 0, 0]}>
            {chartData.map((entry) => {
              const base = entry.color ?? DEFAULT_BAR_COLOR
              const fill = entry.color
                ? (seriesChipTones(entry.color)?.light ?? base)
                : base
              return <Cell key={entry.id} fill={fill} />
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartFigure>
  )
}
